// apps/web/src/pages/estoque/novo-inventario-sheet.tsx — Fase 13, Plano 27 (EST-13, D-24, D-25, D-26).
// FormDialog "Novo inventário": Localização, Dono (Pieltec/Cliente), Itens (todos da localização ou escolhidos). O inventário não
// trava o estoque. Quando a localização ainda não tem movimentos do dono, mostra o banner de saldo de abertura (D-26); o
// servidor decide de verdade (`eAbertura` na resposta da criação) e a tela de contagem repete o banner. Conflito de escopo
// (409 INVENTARIO_ESCOPO_EM_USO) mostra qual inventário já cobre o local/item e oferece abri-lo (R13-08).
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Info, Loader2, X } from 'lucide-react';
import type { ClienteLookupPv, InventarioContagemView, ListaSaldosClientes, ListaSaldosProprios, LoteLookupEstoque, MaterialLookupPv } from '@pieltec/contracts';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { LoteCombobox } from '../../components/estoque/lote-combobox';
import { Button } from '../../components/ui/button';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { MaterialCombobox } from '../../components/vendas/material-combobox';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  APOIO_NAO_TRAVA,
  camposNovoInventarioVazios,
  lerErroInventario,
  montarCriarInventario,
  queryLocalSemMovimentos,
  urlDetalheInventario,
  validarNovoInventario,
  type CamposNovoInventario,
  type ErrosNovoInventario,
  type ErroInventario,
} from '../../lib/estoque/inventario';

const ENDPOINT_MATERIAIS_ESTOQUE = '/api/v1/estoque/lookups/materiais';
const ENDPOINT_CLIENTES_ESTOQUE = '/api/v1/estoque/lookups/clientes';

interface ItemEscolhido {
  readonly id: string;
  readonly rotulo: string;
}

export interface NovoInventarioSheetProps {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}

export function NovoInventarioSheet({ aberto, aoFechar }: NovoInventarioSheetProps) {
  const [campos, setCampos] = useState<CamposNovoInventario>(camposNovoInventarioVazios);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [materiais, setMateriais] = useState<readonly ItemEscolhido[]>([]);
  const [lotes, setLotes] = useState<readonly ItemEscolhido[]>([]);
  const [erros, setErros] = useState<ErrosNovoInventario>({});
  const [erroApi, setErroApi] = useState<ErroInventario | null>(null);
  const [semMovimentos, setSemMovimentos] = useState<boolean | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false);

  // Abre sempre limpo.
  useEffect(() => {
    if (!aberto) return;
    setCampos(camposNovoInventarioVazios());
    setCliente(null);
    setMateriais([]);
    setLotes([]);
    setErros({});
    setErroApi(null);
    setSemMovimentos(null);
  }, [aberto]);

  // Banner de abertura: local sem movimentos do dono. Só orienta; quem decide é o servidor.
  const consulta = queryLocalSemMovimentos(campos);
  const chaveConsulta = consulta ? `${consulta.rota}?${consulta.query}` : '';
  useEffect(() => {
    if (!aberto || chaveConsulta === '') {
      setSemMovimentos(null);
      return;
    }
    let vivo = true;
    setSemMovimentos(null);
    fetch(chaveConsulta, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<ListaSaldosProprios | ListaSaldosClientes>;
      })
      .then((json) => {
        if (vivo) setSemMovimentos(json.total === 0);
      })
      .catch(() => {
        if (vivo) setSemMovimentos(null);
      });
    return () => {
      vivo = false;
    };
  }, [aberto, chaveConsulta]);

  function alterar(parcial: Partial<CamposNovoInventario>): void {
    setCampos((atual) => ({ ...atual, ...parcial }));
    setErros((atual) => {
      const restante: Record<string, string | undefined> = { ...atual };
      for (const chave of Object.keys(parcial)) delete restante[chave];
      if ('escolherItens' in parcial || 'donoTipo' in parcial) delete restante.itens;
      return restante;
    });
    setErroApi(null);
  }

  function trocarDono(donoTipo: 'PIELTEC' | 'CLIENTE'): void {
    setCliente(null);
    setMateriais([]);
    setLotes([]);
    alterar({ donoTipo, clienteId: '', materialIds: [], loteIds: [] });
  }

  function adicionarMaterial(material: MaterialLookupPv): void {
    if (materiais.some((m) => m.id === material.id)) return;
    const proximos = [...materiais, { id: material.id, rotulo: `${material.codigo} — ${material.nome}` }];
    setMateriais(proximos);
    alterar({ materialIds: proximos.map((m) => m.id) });
  }

  function removerMaterial(id: string): void {
    const proximos = materiais.filter((m) => m.id !== id);
    setMateriais(proximos);
    alterar({ materialIds: proximos.map((m) => m.id) });
  }

  function adicionarLote(lote: LoteLookupEstoque): void {
    if (lotes.some((l) => l.id === lote.id)) return;
    const proximos = [...lotes, { id: lote.id, rotulo: `${lote.loteInternoPieltec} — ${lote.clienteNome}` }];
    setLotes(proximos);
    alterar({ loteIds: proximos.map((l) => l.id) });
  }

  function removerLote(id: string): void {
    const proximos = lotes.filter((l) => l.id !== id);
    setLotes(proximos);
    alterar({ loteIds: proximos.map((l) => l.id) });
  }

  async function criar(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (enviandoRef.current) return;
    const validacao = validarNovoInventario(campos);
    setErros(validacao.erros);
    setErroApi(null);
    if (!validacao.ok) return;
    enviandoRef.current = true;
    setEnviando(true);
    try {
      const res = await apiMutate('/api/v1/estoque/inventarios', { body: montarCriarInventario(campos) });
      if (!res.ok) {
        setErroApi(await lerErroInventario(res));
        return;
      }
      const criado = (await res.json()) as InventarioContagemView;
      window.location.assign(urlDetalheInventario(criado.id));
    } catch {
      setErroApi({
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível criar o inventário. Verifique a conexão e tente novamente.',
        conflitoDeVersao: false,
        inventarioConflitante: null,
        itemCodigo: null,
        itemId: null,
      });
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  const ehCliente = campos.donoTipo === 'CLIENTE';
  const escolhidos = ehCliente ? lotes : materiais;

  return (
    <FormDialog open={aberto} onOpenChange={(proximo) => (proximo ? undefined : aoFechar())}>
      <FormDialogContent>
        <form onSubmit={(e) => void criar(e)} className="flex h-full flex-col gap-4" noValidate>
          <FormDialogHeader>
            <FormDialogTitle>Novo inventário</FormDialogTitle>
            <FormDialogDescription>{APOIO_NAO_TRAVA}</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-col gap-4 px-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="inv-localizacao">Localização</Label>
              <LocalizacaoSelect id="inv-localizacao" rotulo="Localização" value={campos.localizacaoId} onChange={(id) => alterar({ localizacaoId: id })} />
              {erros.localizacaoId && <FormMessage>{erros.localizacaoId}</FormMessage>}
            </div>

            <fieldset className="flex flex-col gap-1">
              <legend className="text-sm font-medium">Dono</legend>
              <RadioGroup value={campos.donoTipo} onValueChange={(valor) => trocarDono(valor === 'CLIENTE' ? 'CLIENTE' : 'PIELTEC')} aria-label="Dono" className="flex flex-row gap-6">
                <div className="flex min-h-11 items-center gap-2 sm:min-h-9">
                  <RadioGroupItem value="PIELTEC" id="inv-dono-PIELTEC" />
                  <Label htmlFor="inv-dono-PIELTEC">Pieltec</Label>
                </div>
                <div className="flex min-h-11 items-center gap-2 sm:min-h-9">
                  <RadioGroupItem value="CLIENTE" id="inv-dono-CLIENTE" />
                  <Label htmlFor="inv-dono-CLIENTE">Cliente</Label>
                </div>
              </RadioGroup>
            </fieldset>

            {ehCliente && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="inv-cliente">Cliente</Label>
                <ClienteCombobox
                  id="inv-cliente"
                  endpoint={ENDPOINT_CLIENTES_ESTOQUE}
                  value={cliente}
                  onChange={(escolhido) => {
                    setCliente(escolhido);
                    setLotes([]);
                    alterar({ clienteId: escolhido.id, loteIds: [] });
                  }}
                  invalido={erros.clienteId !== undefined}
                />
                {erros.clienteId && <FormMessage>{erros.clienteId}</FormMessage>}
              </div>
            )}

            <fieldset className="flex flex-col gap-1">
              <legend className="text-sm font-medium">Itens</legend>
              <RadioGroup
                value={campos.escolherItens ? 'ESCOLHER' : 'TODOS'}
                onValueChange={(valor) => alterar({ escolherItens: valor === 'ESCOLHER' })}
                aria-label="Itens do inventário"
              >
                <div className="flex min-h-11 items-center gap-2 sm:min-h-9">
                  <RadioGroupItem value="TODOS" id="inv-itens-TODOS" />
                  <Label htmlFor="inv-itens-TODOS">Todos os itens da localização</Label>
                </div>
                <div className="flex min-h-11 items-center gap-2 sm:min-h-9">
                  <RadioGroupItem value="ESCOLHER" id="inv-itens-ESCOLHER" />
                  <Label htmlFor="inv-itens-ESCOLHER">Escolher itens</Label>
                </div>
              </RadioGroup>
            </fieldset>

            {campos.escolherItens && (
              <div className="flex flex-col gap-2">
                {ehCliente ? (
                  <LoteCombobox id="inv-itens" value={null} onChange={adicionarLote} invalido={erros.itens !== undefined} disabled={campos.clienteId === ''} />
                ) : (
                  <MaterialCombobox<MaterialLookupPv & { unidade?: string }>
                    id="inv-itens"
                    endpoint={ENDPOINT_MATERIAIS_ESTOQUE}
                    value={null}
                    onChange={adicionarMaterial}
                    invalido={erros.itens !== undefined}
                  />
                )}
                {erros.itens && <FormMessage>{erros.itens}</FormMessage>}
                {escolhidos.length > 0 && (
                  <ul className="flex flex-wrap gap-2" aria-label="Itens escolhidos">
                    {escolhidos.map((escolhido) => (
                      <li key={escolhido.id} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-sm">
                        <span>{escolhido.rotulo}</span>
                        <button
                          type="button"
                          onClick={() => (ehCliente ? removerLote(escolhido.id) : removerMaterial(escolhido.id))}
                          aria-label={`Remover ${escolhido.rotulo}`}
                          className="inline-flex size-8 items-center justify-center rounded-full hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                        >
                          <X className="size-4" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {semMovimentos === true && !ehCliente && (
              <div role="status" className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p>Esta localização ainda não tem movimentos. Esta contagem será o saldo de abertura: cada item gera uma entrada inicial com o custo informado.</p>
              </div>
            )}

            {erroApi && (
              <div role="alert" className="flex flex-col gap-2">
                <FormMessage>{erroApi.mensagem}</FormMessage>
                {erroApi.inventarioConflitante && (
                  <Button asChild variant="outline" className="min-h-11 self-start sm:min-h-9">
                    <a href={urlDetalheInventario(erroApi.inventarioConflitante.id)}>Abrir inventário {erroApi.inventarioConflitante.numero}</a>
                  </Button>
                )}
              </div>
            )}
          </div>

          <FormDialogFooter className="sticky bottom-0 border-t bg-background sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" onClick={aoFechar}>
              Voltar
            </Button>
            <Button type="submit" className="min-h-11 sm:min-h-9" disabled={enviando}>
              {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
              Criar inventário
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
